import {
  AuthAccountOrganizationStatusChoices,
  CheckBadgeIcon,
  DangerIcon,
  Form,
  handleApiRequest,
  Typography,
} from "@cpj-ai/panel-npm-package";
import {
  UpdateOrganizationStatusMutation,
  UpdateOrganizationStatusMutationVariables,
  useGetOrganizationInformationAndCredentialsQuery,
  useUpdateOrganizationStatusMutation,
} from "@graphql/generated";
import { Button, Card } from "@heroui/react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import CredentialsInformation from "./CredentialsInformation";
import { getInputConfig, ICompanyInformationFormData } from "./formConfig";
import { useOrganizationInformationFormData } from "./hooks/useOrganizationInformationFormData";
import { useOrganizationInformationSubmit } from "./hooks/useOrganizationInformationSubmit";

interface ICredentialsTabProps {
  refetch?: () => void;
}

const CredentialsTab = ({ refetch }: ICredentialsTabProps) => {
  const { id } = useParams();
  const { data } = useGetOrganizationInformationAndCredentialsQuery({
    variables: { id: id as string },
    skip: !id,
  });
  const [updateOrganizationStatus, { loading: statusLoading }] = useUpdateOrganizationStatusMutation();
  const { handleSaveProfile, loading: informationLoading } = useOrganizationInformationSubmit(
    data?.admin?.organizations?.edges[0]?.node,
  );

  const formHandler = useForm<ICompanyInformationFormData>();

  const companyData = data?.admin?.organizations?.edges[0]?.node;

  useOrganizationInformationFormData({
    organization: companyData,
    reset: formHandler.reset,
  });

  const handleSelectStatus = async ({ selectedStatus }: { selectedStatus: AuthAccountOrganizationStatusChoices }) => {
    if (!id || !selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    await handleApiRequest<UpdateOrganizationStatusMutation, UpdateOrganizationStatusMutationVariables>({
      apiFunction: updateOrganizationStatus,
      variables: {
        id,
        status: selectedStatus,
      },
      componentName: "ChangeStatusModal",
      actionName: "updateOrganizationStatus",
      onSuccess: () => {
        refetch?.();
      },
      successMessage: "Organization status updated successfully",
      fallbackErrorMessage: "Failed to update organization status",
      extraErrorContext: {
        organizationId: id,
        newStatus: selectedStatus,
      },
    });
  };

  return (
    <div>
      <Form
        formHandler={formHandler as any}
        inputConfigs={getInputConfig()}
        wrapInCard={false}
        showCancel={false}
        submitProps={{
          onClick: formHandler.handleSubmit(handleSaveProfile),
          isLoading: informationLoading,
          isDisabled: informationLoading,
          children: informationLoading ? "Saving..." : "Save",
        }}
      />

      <div className="mt-5">
        <Typography className="!font-bold">Verify information</Typography>
        <Card className="py-6 px-5 mt-5">
          <CredentialsInformation data={companyData} />
        </Card>
      </div>

      <div className="flex gap-2 mt-5">
        {companyData?.status !== AuthAccountOrganizationStatusChoices.VERIFIED && (
          <Button
            color="success"
            className="px-6"
            startContent={<CheckBadgeIcon className="w-6 h-6 stroke-white" />}
            onPress={() => handleSelectStatus({ selectedStatus: AuthAccountOrganizationStatusChoices.VERIFIED })}
            isLoading={statusLoading}
          >
            Authentication confirmation
          </Button>
        )}
        <Button
          color="primary"
          className="px-6"
          startContent={<DangerIcon className="w-6 h-6" fill="white" />}
          onPress={() => handleSelectStatus({ selectedStatus: AuthAccountOrganizationStatusChoices.REJECTED })}
          isLoading={statusLoading}
        >
          Remove authentication
        </Button>
      </div>
    </div>
  );
};

export default CredentialsTab;
